/*
世界座標：カメラの状態を考慮しない座標
ローカル座標：canvasの左上を起点とする座標
*/

import DPR from './DPR.js'
import {isTrackpad} from './DetectTrackpad.js';

export default class DragCamera {
  static moveInertiaDeceleration = .9;  // カメラ移動の慣性の減速度
  static zoomVelUnit = .03;  // １度のマウスホイール操作でカメラ移動の慣性に加減算する値
  static zoomInertiaDeceleration = .9;  // ズームの慣性の減速度
  static reverseTrackpadMove = 1;  // ２本指スワイプの移動方向
  static slideSpeed = 10;  // キー操作用の移動速度
  static frameTime = 16;

  constructor(requestDraw, elTarget, getLocalCenterPos = null) {
    //-- public
    this.zoomMin = 1;
    this.zoomMax = 500;

    //-- private
    this._requestDraw = requestDraw;
    this._elTarget = elTarget;
    this._getLocalCenterPos = getLocalCenterPos;
    this._posX = 0;
    this._posY = 0;
    this._velX = 0;  // カメラの16ミリ秒あたりの移動ピクセル
    this._velY = 0;
    this._slideVelX = 0;  // キー操作用
    this._slideVelY = 0;
    this._moveTime = 0;  // カメラを動かした時間を記憶
    this._zoom = 1;
    this._zoomVel = 0;
    this._lastCursorX = 0;
    this._lastCursorY = 0;
    this._correction = 1;  // setTimeoutの差を埋めるための補正値
    this._moveIntervalID = null;
    this._zoomIntervalID = null;

    this._elTarget.addEventListener('wheel', e => this._wheel(e), {passive: true});
    this._elTarget.addEventListener('wheel', e => e.preventDefault());

    //-- this._correctionを計測
    /*const calcCollection = () => {
      console.log('--- Start calculating FPS');  //TENTATIVE
      let intervalID = 0;
      new Promise((ok, ng) => {
        const MAX_COUNT = 64;
        let count = -8;  // 最初のintervalは不安定なので
        let sum = 0;
        let prev = 0;
        intervalID = setInterval(() => {
          if(++count < 0) return;
          console.log('--- Calculating FPS');  //TENTATIVE
          if(count === 0) {
            prev = performance.now();
            return;
          }
          const now = performance.now();
          const gap = now - prev;
          sum += gap;
          prev = now;
          if(count < MAX_COUNT) {
            if(!document.hasFocus()) {
              ng('!hasFocus');
              window.addEventListener('focus', calcCollection, {once: true});
            }
          } else {
            const avr = sum / MAX_COUNT;
            ok(avr);
          }
        }, DragCamera.frameTime);
      }).then(avr => {
        console.log('--- Succeed in calculating FPS');  //TENTATIVE
        if(3 <= Math.abs(DragCamera.frameTime - avr) && avr < 50) {
          this._correction = avr / DragCamera.frameTime;
          console.log('DragCamera._correction: ' + this._correction);  //TENTATIVE
        }
      }).catch(e => {
        console.log('--- Failed in calculating FPS');  //TENTATIVE
        if(e !== '!hasFocus') throw e;
      }).finally(() => {
        console.log('--- Stop calculating FPS');  //TENTATIVE
        clearInterval(intervalID);
      });
    };
    calcCollection();*/
  }

  // キャンバスの中心を取得
  // transformする場合があるため必ずしも要素の中心とは限らない
  get localCenterPos() {
    return this._getLocalCenterPos ? this._getLocalCenterPos() : {
      x: this._elTarget.width / 2,
      y: this._elTarget.height / 2,
    };
  }

  get zoom() { return this._zoom; }
  set zoom(zoom) { this._zoom = zoom;}
  get posX() { return this._posX; }
  set posX(posX) { this._posX = posX; }
  get posY() { return this._posY; }
  set posY(posY) { this._posY = posY; }

  //--- 指定ローカル座標を中心としてカメラを瞬間移動
  teleport(localLeft, localTop, worldX, worldY, zoom) {
    this._zoom = zoom;
    const [localX, localY] = this.worldPtToLocalPt(worldX, worldY);
    [this._posX, this._posY] = this.localPtToWorldPt(localX - localLeft, localY - localTop);
  }

  //--- 世界座標基準でカメラを（瞬間）移動
  moveX(worldX) {
    this._posX += worldX;
  }
  moveY(worldY) {
    this._posY += worldY;
  }
  move(worldX, worldY) {
    this.moveX(worldX);
    this.moveY(worldY);
  }

  //--- 世界座標をローカル座標に変換
  worldXToLocalX(x) {
    return (x - this._posX) * this._zoom;
  }
  worldYToLocalY(y) {
    return (y - this._posY) * this._zoom;
  }
  worldSzToLocalSz(sz) {
    return sz * this._zoom;
  }
  worldPtToLocalPt(x, y) {
    return [
      this.worldXToLocalX(x),
      this.worldYToLocalY(y)
    ];
  }
  worldPtSzToLocalPtSz(x, y, width, height) {
    return [
      ...this.worldPtToLocalPt(x, y),
      this.worldSzToLocalSz(width),
      this.worldSzToLocalSz(height)
    ];
  }

  //--- 描画用
  worldXToLocalXD(x, centering) {
    return Math.floor(this.worldXToLocalX(x)) + (centering ? .5 : 0);
  }
  worldYToLocalYD(y, centering) {
    return Math.floor(this.worldYToLocalY(y)) + (centering ? .5 : 0);
  }
  worldPtToLocalPtD(x, y, centering) {
    return [
      this.worldXToLocalXD(x, centering),
      this.worldYToLocalYD(y, centering)
    ];
  }
  worldPtSzToLocalPtSzD(x, y, width, height, centering) {
    const x1 = Math.floor(this.worldXToLocalX(x));
    const y1 = Math.floor(this.worldYToLocalY(y));
    const x2 = Math.floor(this.worldXToLocalX(x) + this.worldSzToLocalSz(width));
    const y2 = Math.floor(this.worldYToLocalY(y) + this.worldSzToLocalSz(height));
    const c = centering ? .5 : 0;
    return [x1 + c, y1 + c, x2 - x1, y2 - y1];
  }

  //--- ローカル座標を世界座標に変換
  localPtToWorldPt(localX, localY) {
    return [
      this.localXToWorldX(localX),
      this.localYToWorldY(localY)
    ];
  }
  localXToWorldX(localX) {
    return localX / this._zoom + this._posX;
  }
  localYToWorldY(localY) {
    return localY / this._zoom + this._posY;
  }
  localSzToWorldSz(sz) {
    return sz / this._zoom;
  }

  //--- マウスイベント
  start(cursorX, cursorY) {
    this._velX = 0;
    this._velY = 0;
    this._prevX = cursorX;
    this._prevY = cursorY;
  }
  cancel() {
  }
  drag(cursorX, cursorY) {
    // e.movementXは開発ツールで縮小するとおかしいので自前で計算する
    const movementX = cursorX - this._prevX;
    const movementY = cursorY - this._prevY;
    this._prevX = cursorX;
    this._prevY = cursorY;
    this._velX = -movementX / this._zoom;
    this._velY = -movementY / this._zoom;
    this._posX += this._velX;
    this._posY += this._velY;
    this._moveTime = performance.now();
    this._requestDraw();
  }
  finish() {
    if(50 < performance.now() - this._moveTime) {
      this._velX = this._velY = 0;
    }
    if(this._moveIntervalID === null) {
      this._moveIntervalID = setInterval(() => this._handleMoveInertia(), DragCamera.frameTime);
    }
  }
  _wheel(e) {
    let deltaY = e.deltaY;

    // ピンチ操作の場合、感度を高める（ctrlを押していた場合もそうなってしまうがよしとする）
    if(e.ctrlKey) {
      deltaY *= 8;
    }

    if(isTrackpad() === true && !e.ctrlKey) {
      const vx = DragCamera.reverseTrackpadMove * e.deltaX / this._zoom;
      const vy = DragCamera.reverseTrackpadMove * e.deltaY / this._zoom;
      this._posX += vx;
      this._posY += vy;

      if(Math.abs(vx) < 1 && Math.abs(vy) < 1) {
        this._settleWihinOuterLimits();
      }
      
      this._requestDraw();
    } else if(isTrackpad() !== undefined) {
      const {x, y} = this._elTarget.getBoundingClientRect();
      this.wheel({x: e.x - x, y: e.y - y, deltaY});
    }
  }
  wheel({x, y, deltaY}) {
    this._zoomVel += -deltaY / 100 * DragCamera.zoomVelUnit;
    this._lastCursorX = DPR.mul(x);
    this._lastCursorY = DPR.mul(y);
    if(this._zoomIntervalID === null) {
      this._zoomIntervalID = setInterval(() => this._handleZoomInertia(), DragCamera.frameTime);
    }
  }

  //--- カメラの左端 < 世界の右端
  setOuterLimit(localW, localH, worldW, worldH) {
    this._localW = localW;
    this._localH = localH;
    this._worldW = worldW;
    this._worldH = worldH;
    this._settleWihinOuterLimits();
  }
  _settleWihinOuterLimits() {
    let change = false;
    let spaceX = this._localW / 4 / this._zoom;
    let spaceY = this._localH / 4 / this._zoom;

    if(this._posX + this._localW / this._zoom < 0) {
      this._velX = this._initialVelFromDistance(-(this._posX + this._localW / this._zoom) + spaceX);
      change = true;
    } else if(this._worldW < this._posX) {
      this._velX = -this._initialVelFromDistance(this._posX - this._worldW + spaceX);
      change = true;
    }

    if(this._posY + this._localH / this._zoom < 0) {
      this._velY = this._initialVelFromDistance(-(this._posY + this._localH / this._zoom) + spaceY);
      change = true;
    } else if(this._worldH < this._posY) {
      this._velY = -this._initialVelFromDistance(this._posY - this._worldH + spaceY);
      change = true;
    }
    
    if(change && this._moveIntervalID === null) {
      this._moveIntervalID = setInterval(() => this._handleMoveInertia(), DragCamera.frameTime);
    }
  }

  //--- 移動距離から初速度を逆算
  _initialVelFromDistance(distance) {
    let vel = 0.1 / this._zoom;
    do {
      vel /= (DragCamera.moveInertiaDeceleration || .5) ** this._correction;
      distance -= vel;
    } while(0 < distance);
    return vel;
  }

  //--- マウスを離した後のカメラ移動の慣性表現
  _handleMoveInertia() {
    this._posX += this._velX;
    this._posY += this._velY;
    this._velX *= DragCamera.moveInertiaDeceleration ** this._correction;
    this._velY *= DragCamera.moveInertiaDeceleration ** this._correction;

    if(Math.abs(this._velX) < 1 / this._zoom && Math.abs(this._velY) < 1 / this._zoom) {
      this._velX = this._velY = 0;
      clearInterval(this._moveIntervalID);
      this._moveIntervalID = null;
      this._settleWihinOuterLimits();
    }
    this._requestDraw();
  }

  //--- キー操作などメインループ用
  updatePosX(direction) {
    if(direction !== 0) {
      const speed = this.localSzToWorldSz(DragCamera.slideSpeed);
      this._slideVelX += direction * speed * (1 - DragCamera.moveInertiaDeceleration);
      this._slideVelX = Math.max(-speed, Math.min(this._slideVelX, speed));
      this._posX += this._slideVelX;
      this._requestDraw();
    } else if(this._slideVelX !== 0) {
      this._posX += this._slideVelX;
      this._slideVelX *= DragCamera.moveInertiaDeceleration ** this._correction;

      if(Math.abs(this._slideVelX) < 1 / this._zoom) {
        this._slideVelX = 0;
        this._settleWihinOuterLimits();
      }
      this._requestDraw();
    }
  }
  updatePosY(direction) {
    if(direction !== 0) {
      const speed = this.localSzToWorldSz(DragCamera.slideSpeed);
      this._slideVelY += direction * speed * (1 - DragCamera.moveInertiaDeceleration);
      this._slideVelY = Math.max(-speed, Math.min(this._slideVelY, speed));
      this._posY += this._slideVelY;
      this._requestDraw();
    } else if(this._slideVelY !== 0) {
      this._posY += this._slideVelY;
      this._slideVelY *= DragCamera.moveInertiaDeceleration ** this._correction;
  
      if(Math.abs(this._slideVelY) < 1 / this._zoom) {
        this._slideVelY = 0;
        this._settleWihinOuterLimits();
      }
      this._requestDraw();
    }
  }
  resetSlideVel() {
    this._slideVelX = 0;
    this._slideVelY = 0;
  }

  //--- カメラズームの慣性表現
  _handleZoomInertia() {
    let prevCursorWorldX = this._posX + this._lastCursorX / this._zoom;
    let prevCursorWorldY = this._posY + this._lastCursorY / this._zoom;
    this._zoom += this._zoomVel * this._zoom * this._correction;

    this._zoomVel *= DragCamera.zoomInertiaDeceleration ** this._correction;

    if(this.zoomMax < this._zoom) {
      this._zoom = this.zoomMax;
      this._cancelZoomInertia();
    } else if(this._zoom < this.zoomMin) {
      this._zoom = this.zoomMin;
      this._cancelZoomInertia();
    } else if(Math.abs(this._zoomVel) < 0.01) {
      this._cancelZoomInertia();
    }
    this._posX = prevCursorWorldX - this._lastCursorX / this._zoom;
    this._posY = prevCursorWorldY - this._lastCursorY / this._zoom;
    this._requestDraw();
  }

  _cancelZoomInertia() {
    this._zoomVel = 0;
    clearInterval(this._zoomIntervalID);
    this._zoomIntervalID = null;
    this._settleWihinOuterLimits();
  }
}
